import type { EventType, EventUserStatus } from '@graphql/generated';
import type { EventMembership } from '@shared/components/AuthProvider';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useSegmentGroupCallForEvent } from '@shared/core';
import { useCallback, useEffect, useMemo } from 'react';

export type EventPickerProps = Readonly<
  {
    currentEventId?: string;
    onCreateNewEvent: (createMethod: 'event picker dialog') => void;
  } & UseEventPickerControllerReturn
>;
export type UseEventPickerControllerArgs = Readonly<{
  /** @default 'all' */
  membershipStatus?: EventUserStatus | 'adminOrOwner' | 'all';
  onEventClick: (args: { eventId: string; website: string; role: EventUserStatus; membershipsCount: number }) => void;
  onNoEventMembershipsFound?: () => void;
}>;

export type UseEventPickerControllerReturn = ReturnType<typeof useEventPickerController>;

export const useEventPickerController = (props: UseEventPickerControllerArgs) => {
  const { onEventClick, membershipStatus = 'adminOrOwner', onNoEventMembershipsFound } = props;
  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();

  const { hasInitializedOnce } = useAuth();
  const { eventMemberships: events } = useEventMemberships();
  const noMemberShipEventsFound = hasInitializedOnce && !events.length;

  useEffect(() => {
    if (noMemberShipEventsFound) {
      onNoEventMembershipsFound?.();
    }
  }, [hasInitializedOnce, noMemberShipEventsFound, onNoEventMembershipsFound]);

  const filterEvents = useCallback(
    (filterBy: UseEventPickerControllerArgs['membershipStatus']) => {
      const getFilterCondition = (filterBy: UseEventPickerControllerArgs['membershipStatus'], status: EventMembership['status']) => {
        if (filterBy === 'adminOrOwner') {
          return status === 'admin' || status === 'owner';
        } else if (filterBy === 'all' || !filterBy) {
          return true;
        } else {
          return filterBy === status;
        }
      };

      return events.filter(event => getFilterCondition(filterBy, event?.status));
    },
    [events],
  );

  const eventMemberships = useMemo(() => filterEvents(membershipStatus), [filterEvents, membershipStatus]);

  const membershipsCount = eventMemberships.length;
  const handleOnEventClick = useCallback(
    (eventId: string, firebaseId: Maybe<string>, website: string, role: EventUserStatus, eventType: Maybe<EventType>) => {
      executeSegmentGroupCallForEvent(firebaseId, {
        postgresEventId: eventId,
        eventType: eventType,
      });
      onEventClick({ eventId, website, role, membershipsCount });
    },
    [onEventClick, membershipsCount, executeSegmentGroupCallForEvent],
  );

  return {
    handleOnEventClick,
    eventMemberships,
  } as const;
};
